import { Option } from '@app/components/various/Fields/Select';
import { AutocompleteField, FormGrid } from '@app/components/various/Form';
import { useDeliveryWindows } from '@app/utils/hooks/deliveryWindows/useDeliveryWindows';
import { isDefined } from '@app/utils/is';
import { isEmpty } from '@app/utils/isEmpty';
import { DeliveryWindow, Lookbook, Selects } from '@typings';
import React from 'react';
import { useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { DelwinMenuItem } from './DelwinMenuItem';

const getDelwinName = (value: string | number | null, deliveryWindows: DeliveryWindow.DeliveryWindowsSearchItem[]) => {
  const delwinName = deliveryWindows.find(delwin => delwin.id === value)?.name;

  if (!isDefined(delwinName) || isEmpty(delwinName)) {
    return '-';
  }

  return delwinName;
};

export const DeliveryWindowsForm = () => {
  const { t } = useTranslation(['common']);
  const selectedMarketId = useWatch<Lookbook.CreateLookbookForm, 'market'>({ name: 'market' });
  const [selectedDeliveryWindows, setSelectedDeliveryWindows] = React.useState<DeliveryWindow.DeliveryWindowsSearchItem[]>([]);

  const {
    deliveryWindowsOptions,
    isFetching: isFetchingDeliveryWindows,
    handleSearchChange: handleSearchDeliveryWindowsChange,
    handleLoadMore: handleLoadMoreDeliveryWindows,
  } = useDeliveryWindows({
    marketId: selectedMarketId,
  });

  const hasDeliveryWindows = !isEmpty(useWatch<Lookbook.CreateLookbookForm, 'deliveryWindows'>({ name: 'deliveryWindows' }));

  const handleChange = (value: Selects.Selection) => {
    if (!isDefined(value) || (Array.isArray(value) && isEmpty(value)) || !Array.isArray(value)) {
      setSelectedDeliveryWindows([]);

      return;
    }
    setSelectedDeliveryWindows(previousSelected => {
      const tempDeliveryWindowsList = [
        ...previousSelected,
        ...deliveryWindowsOptions.filter(selectedOption => !previousSelected.includes(selectedOption)),
      ];

      return value.map(buyerId => tempDeliveryWindowsList.find(delwinElement => delwinElement.id === buyerId)).filter(isDefined);
    });
  };

  return (
    <FormGrid>
      <AutocompleteField
        name="deliveryWindows"
        overflowContainer
        label={t('common:delivery_window_other')}
        placeholder={hasDeliveryWindows ? '' : t('common:all')}
        renderPill={value => getDelwinName(value, selectedDeliveryWindows)}
        isLoadingOptions={isFetchingDeliveryWindows}
        onSearchChange={handleSearchDeliveryWindowsChange}
        onLoadMore={handleLoadMoreDeliveryWindows}
        onChange={handleChange}
      >
        {deliveryWindowsOptions.map(deliveryWindow => (
          <Option key={deliveryWindow.id} value={deliveryWindow.id}>
            <DelwinMenuItem deliveryWindow={deliveryWindow} />
          </Option>
        ))}
      </AutocompleteField>
    </FormGrid>
  );
};
